<template>
    <div class="pushmanager r v--15">
        <div class="toggle">
            <h2 class="type--h2">Settings</h2>
            <label :data-disabled="disabled">
                <template v-if="notificationsCheckedCount >= 10">
                    This device does not support push notifications
                </template>
                <template v-else-if="isBlocked">
                    Notifications are blocked in your browser settings
                </template>
                <template v-else-if="disabled">
                    <span class="pushmanager__loading-text">Push Notifications</span>
                    <span class="pushmanager__spinner" aria-label="Checking notification status"></span>
                </template>
                <template v-else>
                    Push Notifications
                    <input type="checkbox" :checked="optedIntoNotifications" @change="settingsNotificationChange($event)"/>
                    <i></i>
                </template>
            </label>
            <label>
                <button ref="diagnostics" class="btn--text type--color--sp-mint" @click="toggleDiagnostics()">Diagnostics & troubleshooting</button>
                <div v-show="showingDiagnostics === true" class="pushmanager__debug type--content-links">
                    <div class="pushmanager__debug__details">
                        <div ref="debug" id="debug" class="pushmanager__debug__details__copy type--debug">
                            <p>Device notification setting: {{ notifiationSettings }}</p>
                            <p>User agent: {{ userAgent }}</p>
                            <p>App url: {{ windowUrl }}</p>
                            <p>App version: {{ siloData.user.app.version }}, {{ siloData.user.app.release }}</p>
                            <p>Username: {{ siloData.user.username }}</p>
                            <p>Role: {{ siloData.user.role }}</p>
                            <p>Time zone: {{ siloData.user.tz }}</p>
                            <p>Users current date/time: {{ usersDateTime }}</p>
                            <p>One Signal details:</p>
                            <pre style="overflow: scroll"><span v-text="osSettings"></span></pre>
                        </div>
                        <p class="pushmanager__debug__details__buttons">
                            <button ref="copy" class="btn btn--solid btn--small btn--flex" @click="copyToClipboard()">Copy</button>
                        </p>
                    </div>
                </div>
            </label>
            <label>
                <button ref="refresh" class="btn--text type--color--sp-mint" @click="reloadWindow()">Refresh App</button>
            </label>
        </div>
    </div>
</template>

<script>
export default {
    components: {
    },
    data() {
        return {
            // Disabled makes it so you can't change the toggle
            disabled: true,
            hasNotificationPermissions: false,
            optedIntoNotifications: false,
            notificationsCheckedCount: 0,
            notifiationSettings: Notification.permission, // kept live via checkNotifications() below
            userAgent: navigator.userAgent,
            windowUrl: window.location.href,
            troubleshooting: false,
            debug: false,
            showingDiagnostics: false,
            osSettings: null,
            // Safety net for a hung/rejected optIn()/optOut() promise —
            // even with the .catch() now added in app.js, this stays as
            // a second line of defense so the toggle can never get stuck
            // disabled indefinitely if checkNotifications() somehow
            // doesn't get called back.
            recoveryTimeoutId: null,
        }
    },
    props: {
        instance: Object,
        active: Boolean,
        siloData: Object,
    },
    watch: {
        active() {
            if (this.active === true) {
                console.log('push manager is active, checking notifications');
                this.checkNotifications();
            } else {
                console.log('push manager is not active');
            }
        }
    },
    computed: {
        usersDateTime() {
            const date = new Date();

            const formattedDateTime = new Intl.DateTimeFormat('en-US', {
                year: '2-digit',
                month: 'numeric',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
                timeZoneName: 'short',
                timeZone: this.siloData.user.tz ? this.siloData.user.tz : 'America/New_York'
            }).format(date);

            return formattedDateTime;
        },
        isBlocked() {
            return this.notifiationSettings === 'denied';
        }
    },
    mounted() {
        window.notificationsSettings = this;

        // The watcher above can only catch a *transition* of `active`
        // from false to true. If this component is created with active
        // already true (e.g. rendered directly into an already-visible
        // settings panel, rather than toggled into existence), the
        // watcher never fires — there's no transition to catch — and
        // this stays stuck showing its default disabled state forever.
        // Same class of bug already fixed once in the homescreen
        // component; check directly here too so a fresh mount that's
        // already "active" still initializes correctly.
        if (this.active === true) {
            this.checkNotifications();
        }
    },
    methods: {
        toggleDiagnostics() {
            console.log(this.showingDiagnostics);
            this.showingDiagnostics = !this.showingDiagnostics;
            console.log(this.showingDiagnostics);
        },
        getNotificationSettings() {
            return Notification.permission;
        },
        // Called directly from app.js's pushSubscriptionChangeListener
        // with the state straight from OneSignal's event payload. Using
        // these values directly — rather than re-reading
        // window.notifications.User.PushSubscription live at this exact
        // moment via checkNotifications() — avoids a timing gap where
        // that live object hadn't yet caught up to what the event itself
        // had already confirmed, which was causing optedIn: true to show
        // correctly in the raw osSettings dump while this component's
        // own derived optedIntoNotifications still read false.
        applySubscriptionState(hasToken, optedIn) {
            console.log('*** pushmanager: applying confirmed subscription state', hasToken, optedIn);

            if (this.recoveryTimeoutId) {
                clearTimeout(this.recoveryTimeoutId);
                this.recoveryTimeoutId = null;
            }

            this.disabled = false;
            this.notifiationSettings = Notification.permission;
            this.osSettings = window.notifications;
            if (window.notifications?.Notifications) {
                this.hasNotificationPermissions = window.notifications.Notifications.permission;
            }
            this.optedIntoNotifications = hasToken && optedIn;
        },
        // Entry point — called when this panel becomes active, on mount
        // if already active, and as a callback after opting in/out or
        // OneSignal's own permission/subscription events firing. Always
        // gives polling a fresh budget, so a single earlier failure to
        // load OneSignal doesn't permanently brick the toggle as "not
        // supported" for the rest of the session.
        checkNotifications() {
            console.log('checking notifications value');

            if (this.recoveryTimeoutId) {
                clearTimeout(this.recoveryTimeoutId);
                this.recoveryTimeoutId = null;
            }

            this.disabled = true;
            this.notificationsCheckedCount = 0;

            this.pollForNotifications();
        },
        // Split out from checkNotifications() so retries can keep
        // incrementing the same counter without re-resetting it on every
        // tick — checkNotifications() resets once per external trigger,
        // this just walks the existing budget down.
        pollForNotifications() {
            // When this is called, it should already have access to window.notifications
            if (window.notifications === undefined || window.notifications.Notifications === undefined) {
                // If one signal isn't loaded yet, just keep running the function until it is
                if (this.notificationsCheckedCount < 10) {
                    console.log('re-checking notifications')
                    setTimeout(() => {
                        this.notificationsCheckedCount++;
                        this.pollForNotifications();
                    }, 1000);
                } else {
                    console.log('ending notification checks');
                }

            } else {
                console.log('one signal notifications are active, setting variables based on that');

                this.osSettings = window.notifications;

                this.disabled = false;

                // Refresh the debug panel's permission value here instead
                // of only reading it once at mount — otherwise it goes
                // stale the moment permission changes after initial load.
                this.notifiationSettings = Notification.permission;

                // APP PUSH NOTIFICATION PERMISSIONS
                this.hasNotificationPermissions = window.notifications.Notifications.permission;
                console.log('User can receive push notifications:', this.hasNotificationPermissions);

                // USER OPTED INTO PUSH NOTIFICATIONS
                // optedIn alone defaults to true even for a brand-new
                // subscription that was never actually granted permission
                // — it only reflects whether optOut() was explicitly
                // called, not whether a real subscription exists. Require
                // an actual token too, matching the fix already used in
                // app.js, so this toggle doesn't show "on" for someone
                // who was never really subscribed.
                const hasToken = !!window.notifications.User.PushSubscription.token;
                this.optedIntoNotifications = hasToken && window.notifications.User.PushSubscription.optedIn;
            }
        },

        settingsNotificationChange(event) {
            console.log('setting set to:', event.target.checked);

            this.disabled = true;

            let setting = event.target.checked;

            this.toggleNotificationAction(setting);

            if (setting === true) {
                console.log('sending notifications a function to opt in');
                window.pusher.optIn();
            } else {
                console.log('sending notifications a function to opt out');
                window.pusher.optOut();
            }

            // Safety net: app.js's optIn()/optOut() re-enable this toggle
            // by calling checkNotifications() once their promise settles
            // (now with a .catch() as well as a .then()). This is a
            // second layer on top of that — if for any reason neither
            // callback fires, this forces a re-check after a few seconds
            // instead of leaving the toggle stuck disabled indefinitely.
            if (this.recoveryTimeoutId) {
                clearTimeout(this.recoveryTimeoutId);
            }
            this.recoveryTimeoutId = setTimeout(() => {
                console.log('*** pushmanager: recovery timeout hit, forcing a re-check');
                this.checkNotifications();
            }, 6000);
        },
        toggleNotificationAction(action) {
            console.log('POSTING TOGGLE ACTION');
            let message = `Toggled push notifications to: ${action}`;
            let data = {
                type: 'open',
                message: message,
            };
            window.events.postAction(data);
        },
        reloadWindow() {
            window.location.reload(true);

            this.$refs.refresh.innerText = "Refreshing ...";
            this.$refs.refresh.style.backgroundColor = 'grey';
            this.$refs.refresh.disabled = true;

            setTimeout(() => {
                this.$refs.refresh.innerText = "Refresh App";
                this.$refs.refresh.style = '';
                this.$refs.refresh.disabled = false;
            }, 10000);
        },
        copyToClipboard() {
            // Get the text field
            let copyText = document.querySelector('#debug');

            console.log(copyText);
            console.log(copyText.innerText);

            // Copy the text inside the text field
            navigator.clipboard.writeText(copyText.innerText);

            this.$refs.copy.innerText = "Copied!";
            this.$refs.copy.style.backgroundColor = 'green';
            this.$refs.copy.disabled = true;

            setTimeout(() => {
                this.$refs.copy.innerText = "Copy to Clipboard";
                this.$refs.copy.style = '';
                this.$refs.copy.disabled = false;
            }, 2000);
        }
    }
}
</script>

<style lang="scss">
@import "@s/_mixins.scss";
@import "@s/_vars.scss";

.toggle {
    label {
        display: inline-block;
        width: 100%;
        background: #fff;
        padding: 10px 15px;
        border-top: 1px solid #BBB;

        &:last-child {
            border-bottom: 1px solid #BBB;
        }

        // Hide the ugly checkbox
        >input {
            display: none;
        }

        // New beautiful checkbox
        i {
            display: inline-block;
            float: right;
            padding: 2px;
            width: 40px;
            height: 20px;
            border-radius: 13px;
            vertical-align: middle;
            transition: .25s .09s;
            position: relative;
            //background: #d8d9db;
            background: red;
            box-sizing: initial;
            transition: .25s;

            &:after {
                content: " ";
                display: block;
                width: 20px;
                height: 20px;
                border-radius: 50%;
                background: #fff;
                position: absolute;
                left: 2px;
                transition: .25s;
            }
        }

        // Checked-state
        >input:checked+i {
            background: #4bd865;
        }

        >input:checked+i:after {
            transform: translateX(20px);
        }

        // Label-hover
        &:hover {
            cursor: pointer;
        }

    }
}

label[data-disabled="true"] {
    input {
        pointer-events: none !important;
    }
    input, i {
        opacity: 0.5;
    }
    i {
        background-color: #d8d9db;
    }
}

.pushmanager__loading-text {
    display: inline-block;
    animation: pmPulse 1.4s ease-in-out infinite;
}

.pushmanager__spinner {
    display: inline-block;
    float: right;
    width: 16px;
    height: 16px;
    margin-top: 2px;
    border: 2px solid #d8d9db;
    border-top-color: #26ae7a;
    border-radius: 50%;
    animation: pmSpin 0.7s linear infinite;
}

@keyframes pmSpin {
    to { transform: rotate(360deg); }
}

@keyframes pmPulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.4; }
}

.pushmanager__debug {
    margin-top: rem(20px);
    display: flex;
    flex-direction: column;
    gap: rem(20px);
}

.pushmanager__debug__details {
    padding: 0 0 rem(20px);
    display: flex;
    flex-direction: column;
    gap: rem(25px);
}

.pushmanager__debug__details__copy {
    display: flex;
    flex-direction: column;
}

.pushmanager__debug__details__buttons {
    padding-top: rem(25px);
    border-top: 1px solid #ccc;
    display: flex;
    flex-direction: column;
    gap: rem(15px);
    button {
        transition: all 0.15s;
        &:disabled {
            pointer-events: none;
        }
    }
}
</style>
